import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    ArrowPathIcon,
    BanknotesIcon,
    CheckCircleIcon,
    ClockIcon,
    Cog6ToothIcon,
    DevicePhoneMobileIcon,
    ExclamationTriangleIcon,
    IdentificationIcon,
    PresentationChartLineIcon,
    UserCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { extractApiError } from "@/lib/api-error";
import { getBusinessSettings, updateBusinessSettings } from "../api/businessSettingsApi";
import type {
    BusinessSettingField,
    BusinessSettingsSnapshot,
    UpdateBusinessSettingsRequest,
} from "../types";

type FlashMessage = {
    type: "success" | "error" | "info";
    text: string;
};

type BusinessSettingsFormValues = {
    fareCalculation: {
        baseFare: string;
        perKilometer: string;
        bookingFee: string;
        minimumPerSeat: string;
        roundingUnit: string;
    };
    platform: {
        commissionPercentage: string;
    };
    smsVerification: {
        termiiPinAttempts: string;
    };
    accountLifecycle: {
        userDeleteCooldownHours: string;
    };
    driverLicense: {
        expiryWarningDays: string;
    };
};

type ConfigurationSection =
    | "fareCalculation"
    | "platform"
    | "smsVerification"
    | "accountLifecycle"
    | "driverLicense";

const configurationSections: Array<{
    key: ConfigurationSection;
    title: string;
    description: string;
    icon: React.ElementType;
}> = [
    {
        key: "fareCalculation",
        title: "Fare calculation",
        description: "Pricing and fare rules",
        icon: BanknotesIcon,
    },
    {
        key: "platform",
        title: "Platform revenue",
        description: "Commission settings",
        icon: PresentationChartLineIcon,
    },
    {
        key: "smsVerification",
        title: "SMS verification",
        description: "OTP protection rules",
        icon: DevicePhoneMobileIcon,
    },
    {
        key: "accountLifecycle",
        title: "Account lifecycle",
        description: "Deletion and re-registration",
        icon: UserCircleIcon,
    },
    {
        key: "driverLicense",
        title: "Driver licence",
        description: "Expiry warning policy",
        icon: IdentificationIcon,
    },
];

const formatDateTime = (value: string | null) => {
    if (!value) return "Not changed yet";

    return new Date(value).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatEnumLabel = (value: string | null) => {
    if (!value) return "—";

    return value
        .toLowerCase()
        .split("_")
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(" ");
};

const formatNumber = (value: number, decimalPlaces: number) =>
    new Intl.NumberFormat("en-NG", {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(value);

const formatSettingValue = (value: number, unit: string | null, decimalPlaces: number) => {
    const formattedNumber = formatNumber(value, decimalPlaces);

    if (!unit) return formattedNumber;

    return `${formattedNumber} ${unit}`;
};

const formatEditableValue = (field: BusinessSettingField) =>
    field.validation.decimalPlaces > 0
        ? field.value.toFixed(field.validation.decimalPlaces)
        : String(field.value);

const getAllManagedFields = (snapshot: BusinessSettingsSnapshot): BusinessSettingField[] => [
    snapshot.fareCalculation.baseFare,
    snapshot.fareCalculation.perKilometer,
    snapshot.fareCalculation.bookingFee,
    snapshot.fareCalculation.minimumPerSeat,
    snapshot.fareCalculation.roundingUnit,
    snapshot.platform.commissionPercentage,
    snapshot.smsVerification.termiiPinAttempts,
    snapshot.accountLifecycle.userDeleteCooldownHours,
    snapshot.driverLicense.expiryWarningDays,
];

const snapshotToFormValues = (snapshot: BusinessSettingsSnapshot): BusinessSettingsFormValues => ({
    fareCalculation: {
        baseFare: formatEditableValue(snapshot.fareCalculation.baseFare),
        perKilometer: formatEditableValue(snapshot.fareCalculation.perKilometer),
        bookingFee: formatEditableValue(snapshot.fareCalculation.bookingFee),
        minimumPerSeat: formatEditableValue(snapshot.fareCalculation.minimumPerSeat),
        roundingUnit: formatEditableValue(snapshot.fareCalculation.roundingUnit),
    },
    platform: {
        commissionPercentage: formatEditableValue(snapshot.platform.commissionPercentage),
    },
    smsVerification: {
        termiiPinAttempts: formatEditableValue(snapshot.smsVerification.termiiPinAttempts),
    },
    accountLifecycle: {
        userDeleteCooldownHours: formatEditableValue(
            snapshot.accountLifecycle.userDeleteCooldownHours
        ),
    },
    driverLicense: {
        expiryWarningDays: formatEditableValue(snapshot.driverLicense.expiryWarningDays),
    },
});

const getFieldErrorKey = (groupKey: string, fieldKey: string) => `${groupKey}.${fieldKey}`;

const getSourceTone = (source: string) => {
    if (source === "DATABASE_OVERRIDE") {
        return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300";
    }

    if (source === "APPLICATION_PROPERTY") {
        return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300";
    }

    return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
};

const getFlashTone = (type: FlashMessage["type"]) => {
    if (type === "success") {
        return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300";
    }

    if (type === "error") {
        return "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300";
    }

    return "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300";
};

const validateFieldValue = (field: BusinessSettingField, rawValue: string) => {
    const trimmedValue = rawValue.trim();

    if (!trimmedValue) {
        return `${field.label} is required.`;
    }

    const parsedValue = Number(trimmedValue);

    if (Number.isNaN(parsedValue)) {
        return `Enter a valid number for ${field.label}.`;
    }

    if (field.validation.decimalPlaces === 0 && !Number.isInteger(parsedValue)) {
        return `${field.label} must be a whole number.`;
    }

    const decimalsInInput = trimmedValue.includes(".")
        ? (trimmedValue.split(".")[1]?.length ?? 0)
        : 0;

    if (decimalsInInput > field.validation.decimalPlaces) {
        return `${field.label} supports up to ${field.validation.decimalPlaces} decimal place${
            field.validation.decimalPlaces === 1 ? "" : "s"
        }.`;
    }

    if (field.validation.minValue !== null) {
        const minValue = Number(field.validation.minValue);

        if (parsedValue < minValue) {
            return `${field.label} must be at least ${field.validation.minValue}.`;
        }
    }

    if (field.validation.maxValue !== null) {
        const maxValue = Number(field.validation.maxValue);

        if (parsedValue > maxValue) {
            return `${field.label} must be at most ${field.validation.maxValue}.`;
        }
    }

    return null;
};

const parseChangedValue = (rawValue: string, currentValue: number) => {
    const parsedValue = Number(rawValue.trim());

    if (Number.isNaN(parsedValue) || parsedValue === currentValue) {
        return undefined;
    }

    return parsedValue;
};

const buildUpdatePayload = (
    snapshot: BusinessSettingsSnapshot,
    formValues: BusinessSettingsFormValues
) => {
    const payload: UpdateBusinessSettingsRequest = {};
    let changedCount = 0;

    const baseFare = parseChangedValue(
        formValues.fareCalculation.baseFare,
        snapshot.fareCalculation.baseFare.value
    );
    const perKilometer = parseChangedValue(
        formValues.fareCalculation.perKilometer,
        snapshot.fareCalculation.perKilometer.value
    );
    const bookingFee = parseChangedValue(
        formValues.fareCalculation.bookingFee,
        snapshot.fareCalculation.bookingFee.value
    );
    const minimumPerSeat = parseChangedValue(
        formValues.fareCalculation.minimumPerSeat,
        snapshot.fareCalculation.minimumPerSeat.value
    );
    const roundingUnit = parseChangedValue(
        formValues.fareCalculation.roundingUnit,
        snapshot.fareCalculation.roundingUnit.value
    );

    if (
        baseFare !== undefined ||
        perKilometer !== undefined ||
        bookingFee !== undefined ||
        minimumPerSeat !== undefined ||
        roundingUnit !== undefined
    ) {
        payload.fareCalculation = {};
    }

    if (baseFare !== undefined) {
        payload.fareCalculation!.baseFare = baseFare;
        changedCount += 1;
    }
    if (perKilometer !== undefined) {
        payload.fareCalculation!.perKilometer = perKilometer;
        changedCount += 1;
    }
    if (bookingFee !== undefined) {
        payload.fareCalculation!.bookingFee = bookingFee;
        changedCount += 1;
    }
    if (minimumPerSeat !== undefined) {
        payload.fareCalculation!.minimumPerSeat = minimumPerSeat;
        changedCount += 1;
    }
    if (roundingUnit !== undefined) {
        payload.fareCalculation!.roundingUnit = roundingUnit;
        changedCount += 1;
    }

    const commissionPercentage = parseChangedValue(
        formValues.platform.commissionPercentage,
        snapshot.platform.commissionPercentage.value
    );

    if (commissionPercentage !== undefined) {
        payload.platform = { commissionPercentage };
        changedCount += 1;
    }

    const termiiPinAttempts = parseChangedValue(
        formValues.smsVerification.termiiPinAttempts,
        snapshot.smsVerification.termiiPinAttempts.value
    );

    if (termiiPinAttempts !== undefined) {
        payload.smsVerification = { termiiPinAttempts };
        changedCount += 1;
    }

    const userDeleteCooldownHours = parseChangedValue(
        formValues.accountLifecycle.userDeleteCooldownHours,
        snapshot.accountLifecycle.userDeleteCooldownHours.value
    );

    if (userDeleteCooldownHours !== undefined) {
        payload.accountLifecycle = { userDeleteCooldownHours };
        changedCount += 1;
    }

    const expiryWarningDays = parseChangedValue(
        formValues.driverLicense.expiryWarningDays,
        snapshot.driverLicense.expiryWarningDays.value
    );

    if (expiryWarningDays !== undefined) {
        payload.driverLicense = { expiryWarningDays };
        changedCount += 1;
    }

    return { payload, changedCount };
};

const validateFormValues = (
    snapshot: BusinessSettingsSnapshot,
    formValues: BusinessSettingsFormValues
) => {
    const nextErrors: Record<string, string> = {};

    const validations: Array<[string, BusinessSettingField, string]> = [
        [
            getFieldErrorKey("fareCalculation", "baseFare"),
            snapshot.fareCalculation.baseFare,
            formValues.fareCalculation.baseFare,
        ],
        [
            getFieldErrorKey("fareCalculation", "perKilometer"),
            snapshot.fareCalculation.perKilometer,
            formValues.fareCalculation.perKilometer,
        ],
        [
            getFieldErrorKey("fareCalculation", "bookingFee"),
            snapshot.fareCalculation.bookingFee,
            formValues.fareCalculation.bookingFee,
        ],
        [
            getFieldErrorKey("fareCalculation", "minimumPerSeat"),
            snapshot.fareCalculation.minimumPerSeat,
            formValues.fareCalculation.minimumPerSeat,
        ],
        [
            getFieldErrorKey("fareCalculation", "roundingUnit"),
            snapshot.fareCalculation.roundingUnit,
            formValues.fareCalculation.roundingUnit,
        ],
        [
            getFieldErrorKey("platform", "commissionPercentage"),
            snapshot.platform.commissionPercentage,
            formValues.platform.commissionPercentage,
        ],
        [
            getFieldErrorKey("smsVerification", "termiiPinAttempts"),
            snapshot.smsVerification.termiiPinAttempts,
            formValues.smsVerification.termiiPinAttempts,
        ],
        [
            getFieldErrorKey("accountLifecycle", "userDeleteCooldownHours"),
            snapshot.accountLifecycle.userDeleteCooldownHours,
            formValues.accountLifecycle.userDeleteCooldownHours,
        ],
        [
            getFieldErrorKey("driverLicense", "expiryWarningDays"),
            snapshot.driverLicense.expiryWarningDays,
            formValues.driverLicense.expiryWarningDays,
        ],
    ];

    validations.forEach(([key, field, rawValue]) => {
        const error = validateFieldValue(field, rawValue);

        if (error) {
            nextErrors[key] = error;
        }
    });

    return nextErrors;
};

const SectionCard = ({
    title,
    subtitle,
    icon: Icon,
    children,
}: {
    title: string;
    subtitle: string;
    icon: React.ElementType;
    children: React.ReactNode;
}) => (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
            <div className="flex items-start gap-3">
                <div className="rounded-2xl bg-primary-50 p-3 text-primary-600 dark:bg-primary-900/20 dark:text-primary-300">
                    <Icon className="h-5 w-5" />
                </div>
                <div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        {title}
                    </h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
                </div>
            </div>
        </div>
        <div className="p-6">{children}</div>
    </section>
);

const StatCard = ({
    title,
    value,
    helper,
    icon: Icon,
}: {
    title: string;
    value: string;
    helper: string;
    icon: React.ElementType;
}) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-start justify-between gap-4">
            <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{title}</p>
                <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-slate-100">
                    {value}
                </p>
                <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">{helper}</p>
            </div>
            <div className="rounded-2xl bg-primary-50 p-3 text-primary-600 dark:bg-primary-900/20 dark:text-primary-300">
                <Icon className="h-5 w-5" />
            </div>
        </div>
    </div>
);

const SettingEditorCard = ({
    field,
    value,
    error,
    dirty,
    onChange,
}: {
    field: BusinessSettingField;
    value: string;
    error?: string;
    dirty: boolean;
    onChange: (value: string) => void;
}) => (
    <div className="rounded-2xl border border-slate-200 p-5 dark:border-slate-700">
        <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
                <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                        {field.label}
                    </h3>
                    <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${getSourceTone(
                            field.source
                        )}`}
                    >
                        {formatEnumLabel(field.source)}
                    </span>
                    <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                            field.overridden
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                        }`}
                    >
                        {field.overridden ? "Override Active" : "Using Fallback"}
                    </span>
                    {dirty ? (
                        <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                            Unsaved
                        </span>
                    ) : null}
                </div>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                    {field.description}
                </p>
            </div>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
                type="number"
                step={
                    field.validation.decimalPlaces === 0
                        ? "1"
                        : `0.${"0".repeat(Math.max(field.validation.decimalPlaces - 1, 0))}1`
                }
                value={value}
                onChange={(event) => onChange(event.target.value)}
                className={`w-full rounded-xl border px-4 py-3 text-sm text-slate-900 transition focus:outline-none focus:ring-4 dark:bg-slate-900 dark:text-slate-100 ${
                    error
                        ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/15 dark:border-rose-700"
                        : "border-slate-200 focus:border-primary-500 focus:ring-primary-500/15 dark:border-slate-700"
                }`}
            />
            {field.unit ? (
                <span className="inline-flex items-center rounded-xl bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    {field.unit}
                </span>
            ) : null}
        </div>

        {error ? <p className="mt-2 text-sm text-rose-600 dark:text-rose-400">{error}</p> : null}

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/40">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Current Value
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {formatSettingValue(field.value, field.unit, field.validation.decimalPlaces)}
                </p>
            </div>
            <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/40">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Default Value
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {formatSettingValue(
                        field.defaultValue,
                        field.unit,
                        field.validation.decimalPlaces
                    )}
                </p>
            </div>
            <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/40">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Last Updated
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {formatDateTime(field.updatedAt)}
                </p>
            </div>
            <div className="rounded-xl bg-slate-50 px-4 py-3 dark:bg-slate-800/40">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Updated By
                </p>
                <p className="mt-1 break-all text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {field.updatedBy ?? "—"}
                </p>
            </div>
        </div>

        <div className="mt-4 rounded-xl border border-dashed border-slate-200 px-4 py-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
            Validation:
            {" min "}
            {field.validation.minValue ?? "—"}
            {", max "}
            {field.validation.maxValue ?? "—"}
            {", decimal places "}
            {field.validation.decimalPlaces}
        </div>
    </div>
);

export const PlatformConfigurationRoute = () => {
    const queryClient = useQueryClient();
    const [formValues, setFormValues] = useState<BusinessSettingsFormValues | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [flashMessage, setFlashMessage] = useState<FlashMessage | null>(null);
    const [activeSection, setActiveSection] = useState<ConfigurationSection>("fareCalculation");

    const settingsQuery = useQuery({
        queryKey: ["business-settings"],
        queryFn: getBusinessSettings,
    });

    const settings = settingsQuery.data?.data;

    useEffect(() => {
        if (settings) {
            setFormValues(snapshotToFormValues(settings));
            setErrors({});
        }
    }, [settings]);

    const updateMutation = useMutation({
        mutationFn: updateBusinessSettings,
        onSuccess: (response) => {
            queryClient.setQueryData(["business-settings"], response);
            setFormValues(snapshotToFormValues(response.data));
            setErrors({});
            setFlashMessage({
                type: "success",
                text: response.message || "Business settings updated successfully.",
            });
        },
        onError: (error: unknown) => {
            setFlashMessage({
                type: "error",
                text: extractApiError(error),
            });
        },
    });

    const managedFields = useMemo(
        () => (settings ? getAllManagedFields(settings) : []),
        [settings]
    );

    const overriddenCount = managedFields.filter((field) => field.overridden).length;
    const mostRecentUpdate = managedFields
        .map((field) => field.updatedAt)
        .filter((value): value is string => Boolean(value))
        .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0];

    const dirtyCount =
        settings && formValues ? buildUpdatePayload(settings, formValues).changedCount : 0;

    const handleFieldChange = <
        SectionKey extends keyof BusinessSettingsFormValues,
        FieldKey extends keyof BusinessSettingsFormValues[SectionKey],
    >(
        section: SectionKey,
        field: FieldKey,
        value: string
    ) => {
        setFormValues((previousValues) => {
            if (!previousValues) {
                return previousValues;
            }

            return {
                ...previousValues,
                [section]: {
                    ...previousValues[section],
                    [field]: value,
                },
            } as BusinessSettingsFormValues;
        });

        setErrors((previousErrors) => {
            const nextErrors = { ...previousErrors };
            delete nextErrors[getFieldErrorKey(String(section), String(field))];
            return nextErrors;
        });
        setFlashMessage(null);
    };

    const handleReset = () => {
        if (!settings) {
            return;
        }

        setFormValues(snapshotToFormValues(settings));
        setErrors({});
        setFlashMessage(null);
    };

    const handleSave = () => {
        if (!settings || !formValues) {
            return;
        }

        const nextErrors = validateFormValues(settings, formValues);

        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors);
            setFlashMessage({
                type: "error",
                text: "Please fix the validation errors before saving.",
            });
            return;
        }

        const { payload, changedCount } = buildUpdatePayload(settings, formValues);

        if (changedCount === 0) {
            setFlashMessage({
                type: "info",
                text: "No changes to save yet.",
            });
            return;
        }

        updateMutation.mutate(payload);
    };

    if (settingsQuery.isLoading && !settings) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if ((settingsQuery.error || !settings || !formValues) && !settingsQuery.isLoading) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <h3 className="mt-3 text-lg font-semibold text-rose-700 dark:text-rose-300">
                    Failed to load platform configuration
                </h3>
                <p className="mt-2 text-sm text-rose-600 dark:text-rose-400">
                    {extractApiError(settingsQuery.error)}
                </p>
                <div className="mt-6">
                    <Button variant="secondary" size="sm" onClick={() => settingsQuery.refetch()}>
                        <ArrowPathIcon className="mr-1.5 h-4 w-4" />
                        Try Again
                    </Button>
                </div>
            </div>
        );
    }

    const resolvedSettings = settings!;
    const resolvedFormValues = formValues!;

    return (
        <div className="space-y-6">
            <section className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                    <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-slate-50">
                        Platform Configuration
                    </h1>
                    <p className="mt-1 max-w-3xl text-sm text-slate-500 dark:text-slate-300">
                        Manage business runtime settings consumed by fare calculation, platform
                        commission, verification, expiry, and account lifecycle controls without a
                        redeploy.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                            setFlashMessage(null);
                            settingsQuery.refetch();
                        }}
                        disabled={settingsQuery.isFetching}
                    >
                        <ArrowPathIcon
                            className={`mr-1.5 h-4 w-4 ${settingsQuery.isFetching ? "animate-spin" : ""}`}
                        />
                        Refresh
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReset}
                        disabled={dirtyCount === 0 || updateMutation.isPending}
                    >
                        Reset Form
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSave}
                        disabled={updateMutation.isPending}
                    >
                        {updateMutation.isPending ? "Saving..." : "Save Changes"}
                    </Button>
                </div>
            </section>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard
                    title="Managed Keys"
                    value={String(managedFields.length)}
                    helper="Runtime settings currently exposed by the backend."
                    icon={Cog6ToothIcon}
                />
                <StatCard
                    title="DB Overrides"
                    value={String(overriddenCount)}
                    helper={`${managedFields.length - overriddenCount} keys still use fallback sources.`}
                    icon={CheckCircleIcon}
                />
                <StatCard
                    title="Pending Edits"
                    value={String(dirtyCount)}
                    helper="Only changed fields are included in the next PUT request."
                    icon={PresentationChartLineIcon}
                />
                <StatCard
                    title="Last Change"
                    value={mostRecentUpdate ? formatDateTime(mostRecentUpdate) : "None yet"}
                    helper={`Snapshot generated ${formatDateTime(resolvedSettings.generatedAt)}.`}
                    icon={ClockIcon}
                />
            </div>

            {flashMessage ? (
                <div
                    className={`rounded-2xl border p-4 text-sm ${getFlashTone(flashMessage.type)}`}
                >
                    {flashMessage.text}
                </div>
            ) : null}

            <div className="grid gap-6 xl:grid-cols-[250px_minmax(0,1fr)] xl:items-start">
                <aside className="space-y-3 xl:sticky xl:top-6">
                    <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <p className="px-3 pb-2 pt-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
                            Configuration areas
                        </p>
                        <div className="space-y-1">
                            {configurationSections.map(
                                ({ key, title, description, icon: Icon }) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => setActiveSection(key)}
                                        className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition ${
                                            activeSection === key
                                                ? "bg-primary-50 text-primary-700 dark:bg-primary-900/25 dark:text-primary-300"
                                                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
                                        }`}
                                    >
                                        <span
                                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                                                activeSection === key
                                                    ? "bg-white text-primary-600 shadow-sm dark:bg-slate-800 dark:text-primary-300"
                                                    : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                                            }`}
                                        >
                                            <Icon className="h-5 w-5" />
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block text-sm font-semibold">
                                                {title}
                                            </span>
                                            <span className="mt-0.5 block text-xs text-slate-400 dark:text-slate-500">
                                                {description}
                                            </span>
                                        </span>
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs leading-5 text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
                        Select an area to review or edit its runtime settings. Unsaved changes are
                        kept while you move between areas.
                    </div>
                </aside>

                <div className="min-w-0 space-y-6">
                    {activeSection === "fareCalculation" ? (
                        <SectionCard
                            title="Fare Calculation"
                            subtitle="Core pricing inputs used by fare preview and final ride calculation."
                            icon={BanknotesIcon}
                        >
                            <div className="grid gap-4 xl:grid-cols-2">
                                <SettingEditorCard
                                    field={resolvedSettings.fareCalculation.baseFare}
                                    value={resolvedFormValues.fareCalculation.baseFare}
                                    error={errors[getFieldErrorKey("fareCalculation", "baseFare")]}
                                    dirty={
                                        parseChangedValue(
                                            resolvedFormValues.fareCalculation.baseFare,
                                            resolvedSettings.fareCalculation.baseFare.value
                                        ) !== undefined
                                    }
                                    onChange={(value) =>
                                        handleFieldChange("fareCalculation", "baseFare", value)
                                    }
                                />
                                <SettingEditorCard
                                    field={resolvedSettings.fareCalculation.perKilometer}
                                    value={resolvedFormValues.fareCalculation.perKilometer}
                                    error={
                                        errors[getFieldErrorKey("fareCalculation", "perKilometer")]
                                    }
                                    dirty={
                                        parseChangedValue(
                                            resolvedFormValues.fareCalculation.perKilometer,
                                            resolvedSettings.fareCalculation.perKilometer.value
                                        ) !== undefined
                                    }
                                    onChange={(value) =>
                                        handleFieldChange("fareCalculation", "perKilometer", value)
                                    }
                                />
                                <SettingEditorCard
                                    field={resolvedSettings.fareCalculation.bookingFee}
                                    value={resolvedFormValues.fareCalculation.bookingFee}
                                    error={
                                        errors[getFieldErrorKey("fareCalculation", "bookingFee")]
                                    }
                                    dirty={
                                        parseChangedValue(
                                            resolvedFormValues.fareCalculation.bookingFee,
                                            resolvedSettings.fareCalculation.bookingFee.value
                                        ) !== undefined
                                    }
                                    onChange={(value) =>
                                        handleFieldChange("fareCalculation", "bookingFee", value)
                                    }
                                />
                                <SettingEditorCard
                                    field={resolvedSettings.fareCalculation.minimumPerSeat}
                                    value={resolvedFormValues.fareCalculation.minimumPerSeat}
                                    error={
                                        errors[
                                            getFieldErrorKey("fareCalculation", "minimumPerSeat")
                                        ]
                                    }
                                    dirty={
                                        parseChangedValue(
                                            resolvedFormValues.fareCalculation.minimumPerSeat,
                                            resolvedSettings.fareCalculation.minimumPerSeat.value
                                        ) !== undefined
                                    }
                                    onChange={(value) =>
                                        handleFieldChange(
                                            "fareCalculation",
                                            "minimumPerSeat",
                                            value
                                        )
                                    }
                                />
                                <SettingEditorCard
                                    field={resolvedSettings.fareCalculation.roundingUnit}
                                    value={resolvedFormValues.fareCalculation.roundingUnit}
                                    error={
                                        errors[getFieldErrorKey("fareCalculation", "roundingUnit")]
                                    }
                                    dirty={
                                        parseChangedValue(
                                            resolvedFormValues.fareCalculation.roundingUnit,
                                            resolvedSettings.fareCalculation.roundingUnit.value
                                        ) !== undefined
                                    }
                                    onChange={(value) =>
                                        handleFieldChange("fareCalculation", "roundingUnit", value)
                                    }
                                />
                            </div>
                        </SectionCard>
                    ) : null}

                    {activeSection === "platform" ? (
                        <SectionCard
                            title="Platform Revenue"
                            subtitle="Controls the platform commission percentage applied to ride payments."
                            icon={PresentationChartLineIcon}
                        >
                            <div className="grid gap-4 xl:grid-cols-2">
                                <SettingEditorCard
                                    field={resolvedSettings.platform.commissionPercentage}
                                    value={resolvedFormValues.platform.commissionPercentage}
                                    error={
                                        errors[getFieldErrorKey("platform", "commissionPercentage")]
                                    }
                                    dirty={
                                        parseChangedValue(
                                            resolvedFormValues.platform.commissionPercentage,
                                            resolvedSettings.platform.commissionPercentage.value
                                        ) !== undefined
                                    }
                                    onChange={(value) =>
                                        handleFieldChange("platform", "commissionPercentage", value)
                                    }
                                />
                            </div>
                        </SectionCard>
                    ) : null}

                    {activeSection === "smsVerification" ? (
                        <SectionCard
                            title="SMS Verification"
                            subtitle="Controls runtime verification behavior for Termii OTP flows."
                            icon={DevicePhoneMobileIcon}
                        >
                            <div className="grid gap-4 xl:grid-cols-2">
                                <SettingEditorCard
                                    field={resolvedSettings.smsVerification.termiiPinAttempts}
                                    value={resolvedFormValues.smsVerification.termiiPinAttempts}
                                    error={
                                        errors[
                                            getFieldErrorKey("smsVerification", "termiiPinAttempts")
                                        ]
                                    }
                                    dirty={
                                        parseChangedValue(
                                            resolvedFormValues.smsVerification.termiiPinAttempts,
                                            resolvedSettings.smsVerification.termiiPinAttempts.value
                                        ) !== undefined
                                    }
                                    onChange={(value) =>
                                        handleFieldChange(
                                            "smsVerification",
                                            "termiiPinAttempts",
                                            value
                                        )
                                    }
                                />
                            </div>
                        </SectionCard>
                    ) : null}

                    {activeSection === "accountLifecycle" ? (
                        <SectionCard
                            title="Account Lifecycle"
                            subtitle="Defines business timing around deleted-account re-registration."
                            icon={UserCircleIcon}
                        >
                            <div className="grid gap-4 xl:grid-cols-2">
                                <SettingEditorCard
                                    field={
                                        resolvedSettings.accountLifecycle.userDeleteCooldownHours
                                    }
                                    value={
                                        resolvedFormValues.accountLifecycle.userDeleteCooldownHours
                                    }
                                    error={
                                        errors[
                                            getFieldErrorKey(
                                                "accountLifecycle",
                                                "userDeleteCooldownHours"
                                            )
                                        ]
                                    }
                                    dirty={
                                        parseChangedValue(
                                            resolvedFormValues.accountLifecycle
                                                .userDeleteCooldownHours,
                                            resolvedSettings.accountLifecycle
                                                .userDeleteCooldownHours.value
                                        ) !== undefined
                                    }
                                    onChange={(value) =>
                                        handleFieldChange(
                                            "accountLifecycle",
                                            "userDeleteCooldownHours",
                                            value
                                        )
                                    }
                                />
                            </div>
                        </SectionCard>
                    ) : null}

                    {activeSection === "driverLicense" ? (
                        <SectionCard
                            title="Driver Licence Expiry"
                            subtitle="Controls when drivers receive an expiry warning. The post-expiry grace period is fixed backend policy."
                            icon={IdentificationIcon}
                        >
                            <div className="grid gap-4 xl:grid-cols-2">
                                <SettingEditorCard
                                    field={resolvedSettings.driverLicense.expiryWarningDays}
                                    value={resolvedFormValues.driverLicense.expiryWarningDays}
                                    error={
                                        errors[
                                            getFieldErrorKey("driverLicense", "expiryWarningDays")
                                        ]
                                    }
                                    dirty={
                                        parseChangedValue(
                                            resolvedFormValues.driverLicense.expiryWarningDays,
                                            resolvedSettings.driverLicense.expiryWarningDays.value
                                        ) !== undefined
                                    }
                                    onChange={(value) =>
                                        handleFieldChange(
                                            "driverLicense",
                                            "expiryWarningDays",
                                            value
                                        )
                                    }
                                />
                                <div className="rounded-2xl border border-slate-200 p-5 dark:border-slate-700">
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                                                Expiry grace period
                                            </h3>
                                            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                                                Ride access remains enabled for this many calendar
                                                days after the licence expiry date. This value is
                                                read-only.
                                            </p>
                                        </div>
                                        <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                            Backend policy
                                        </span>
                                    </div>
                                    <p className="mt-6 text-3xl font-bold text-slate-900 dark:text-slate-100">
                                        {resolvedSettings.driverLicense.expiryGraceDays}{" "}
                                        <span className="text-base font-medium text-slate-500">
                                            days
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </SectionCard>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
